import { useState, useEffect } from 'react';
import { CheckCircle, XCircle, AlertCircle, Eye, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../services/api';

const KIND_LABELS = {
  id_front: 'ID Front',
  id_back: 'ID Back',
  selfie_with_id: 'Selfie with ID',
  proof_of_address: 'Proof of Address',
};

const KYCDocumentThumbnail = ({ kind, docId }) => {
  const [blobUrl, setBlobUrl] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isPdf, setIsPdf] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let url = null;
    (async () => {
      try {
        const token = localStorage.getItem('adminToken');
        const res = await api.get(`/admin/kyc/documents/${docId}`, {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob',
        });
        if (cancelled) return;
        setIsPdf(res.data.type === 'application/pdf');
        url = URL.createObjectURL(res.data);
        setBlobUrl(url);
      } catch (e) {
        if (!cancelled) setError(e.response?.data?.detail || 'Failed to load');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [docId]);

  return (
    <div data-testid={`kyc-thumb-${kind}`}>
      <p className="text-gray-400 text-xs sm:text-sm mb-2">{KIND_LABELS[kind] || kind}</p>
      <div className="bg-gray-700 rounded-lg overflow-hidden aspect-video flex items-center justify-center">
        {loading ? (
          <Loader2 className="h-5 w-5 text-gray-400 animate-spin" />
        ) : error ? (
          <span className="text-rose-300 text-xs px-2 text-center">{error}</span>
        ) : isPdf ? (
          <a href={blobUrl} target="_blank" rel="noreferrer" className="text-purple-300 underline text-sm">
            Open PDF ↗
          </a>
        ) : (
          <a href={blobUrl} target="_blank" rel="noreferrer" className="block w-full h-full">
            <img src={blobUrl} alt={kind} className="w-full h-full object-contain hover:opacity-90 transition-opacity" />
          </a>
        )}
      </div>
    </div>
  );
};

const KYCManagement = () => {
  const [pendingKYC, setPendingKYC] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedKYC, setSelectedKYC] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [feedback, setFeedback] = useState('');
  const [kycLevel, setKycLevel] = useState(1);
  const [kycRequired, setKycRequired] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);

  useEffect(() => {
    fetchPendingKYC();
    fetchKycSettings();
  }, []);

  const fetchKycSettings = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const res = await api.get('/admin/kyc/settings', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setKycRequired(!!res.data?.kyc_required);
    } catch (e) {
      console.error('Fetch KYC settings error:', e);
    }
  };

  const toggleKycRequired = async () => {
    try {
      setSavingSettings(true);
      const token = localStorage.getItem('adminToken');
      const next = !kycRequired;
      const res = await api.put(
        '/admin/kyc/settings',
        { kyc_required: next },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setKycRequired(!!res.data?.kyc_required);
      toast.success(res.data?.message || 'KYC settings updated');
    } catch (e) {
      console.error('Toggle KYC required error:', e);
      toast.error('Failed to update KYC settings');
    } finally {
      setSavingSettings(false);
    }
  };

  const fetchPendingKYC = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await api.get('/admin/kyc/pending', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setPendingKYC(response.data);
    } catch (error) {
      console.error('Error fetching KYC:', error);
      toast.error('Failed to fetch pending KYC');
    } finally {
      setLoading(false);
    }
  };

  const handleReview = async (userId, status) => {
    try {
      const token = localStorage.getItem('adminToken');
      await api.put(`/admin/kyc/${userId}/review`,
        {
          kyc_status: status,
          kyc_level: status === 'approved' ? kycLevel : 0,
          feedback: feedback
        },
        { headers: { 'Authorization': `Bearer ${token}` }}
      );
      toast.success(`KYC ${status} successfully`);
      setShowModal(false);
      setFeedback('');
      fetchPendingKYC();
    } catch (error) {
      console.error('Error reviewing KYC:', error);
      toast.error('Failed to review KYC');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-white">KYC Management</h1>
          <p className="text-gray-400 text-sm">Review and approve KYC submissions</p>
        </div>
        <div className="bg-purple-600/20 px-3 sm:px-4 py-2 rounded-lg border border-purple-500/30 w-fit">
          <p className="text-purple-400 font-semibold text-sm">{pendingKYC.length} Pending</p>
        </div>
      </div>

      {/* KYC Policy Toggle */}
      <div className={`rounded-xl p-5 border ${kycRequired ? 'bg-amber-500/10 border-amber-500/40' : 'bg-gray-900 border-gray-800'}`} data-testid="kyc-policy-card">
        <div className="flex items-center justify-between gap-4">
          <div>
            <p className="text-white font-bold text-sm sm:text-base flex items-center gap-2">
              KYC application is currently
              <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${kycRequired ? 'bg-amber-400/20 text-amber-200' : 'bg-emerald-500/20 text-emerald-300'}`}>
                {kycRequired ? 'COMPULSORY' : 'OPTIONAL'}
              </span>
            </p>
            <p className="text-gray-400 text-xs sm:text-sm mt-1">
              {kycRequired
                ? 'New users must complete KYC before they can trade. The signup page shows a warning + acknowledgement checkbox.'
                : 'Users can skip KYC and still trade. A soft prompt appears on their dashboard.'}
            </p>
          </div>
          <button
            onClick={toggleKycRequired}
            disabled={savingSettings}
            data-testid="toggle-kyc-required-btn"
            className={`relative inline-flex h-7 w-14 shrink-0 items-center rounded-full transition-colors disabled:opacity-50 ${kycRequired ? 'bg-amber-500' : 'bg-gray-600'}`}
            aria-label="Toggle KYC requirement"
          >
            <span className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${kycRequired ? 'translate-x-8' : 'translate-x-1'}`} />
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-purple-500 border-t-transparent"></div>
        </div>
      ) : pendingKYC.length === 0 ? (
        <div className="bg-gray-900 rounded-xl p-12 text-center border border-gray-800">
          <AlertCircle className="h-12 w-12 text-gray-600 mx-auto mb-4" />
          <p className="text-gray-400">No pending KYC submissions</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4">
          {pendingKYC.map((kyc) => (
            <div key={kyc.user_id} className="bg-gray-900 rounded-xl p-4 sm:p-6 border border-gray-800 hover:border-purple-500 transition-colors">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div className="flex-1 w-full">
                  <div className="flex items-center space-x-4 mb-4">
                    <div>
                      <p className="text-white font-bold text-base sm:text-lg">{kyc.name}</p>
                      <p className="text-gray-400 text-xs sm:text-sm break-all">{kyc.email}</p>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
                    <div>
                      <p className="text-gray-400 text-xs">Full Name</p>
                      <p className="text-white font-semibold text-sm">{kyc.kyc_data?.full_name || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-gray-400 text-xs">Nationality</p>
                      <p className="text-white font-semibold text-sm">{kyc.kyc_data?.nationality || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-gray-400 text-xs">ID Type</p>
                      <p className="text-white font-semibold text-sm">{kyc.kyc_data?.id_type || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-gray-400 text-xs">Submitted</p>
                      <p className="text-white font-semibold text-sm">
                        {new Date(kyc.kyc_submitted_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                </div>
                
                <button
                  onClick={() => {
                    setSelectedKYC(kyc);
                    setShowModal(true);
                  }}
                  className="w-full sm:w-auto px-4 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg text-white font-semibold flex items-center justify-center space-x-2 text-sm"
                >
                  <Eye className="h-4 w-4" />
                  <span>Review</span>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Review Modal */}
      {showModal && selectedKYC && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-gray-900 rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto border border-gray-800">
            <div className="p-4 sm:p-6 border-b border-gray-800 flex items-center justify-between">
              <h3 className="text-lg sm:text-xl font-bold text-white">Review KYC Submission</h3>
              <button
                onClick={() => setShowModal(false)}
                className="text-gray-400 hover:text-white"
              >
                <XCircle className="h-6 w-6" />
              </button>
            </div>
            
            <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
              {/* User Info */}
              <div className="bg-gray-800 rounded-lg p-4">
                <h4 className="text-white font-bold mb-3 text-sm sm:text-base">User Information</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                  <div>
                    <p className="text-gray-400 text-xs sm:text-sm">Email</p>
                    <p className="text-white text-sm sm:text-base break-all">{selectedKYC.email}</p>
                  </div>
                  <div>
                    <p className="text-gray-400 text-xs sm:text-sm">Full Name</p>
                    <p className="text-white text-sm sm:text-base">{selectedKYC.kyc_data?.full_name}</p>
                  </div>
                  <div>
                    <p className="text-gray-400 text-xs sm:text-sm">Nationality</p>
                    <p className="text-white text-sm sm:text-base">{selectedKYC.kyc_data?.nationality || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-gray-400 text-xs sm:text-sm">Date of Birth</p>
                    <p className="text-white text-sm sm:text-base">{selectedKYC.kyc_data?.date_of_birth || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-gray-400 text-xs sm:text-sm">ID Type</p>
                    <p className="text-white text-sm sm:text-base">{selectedKYC.kyc_data?.id_type || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-gray-400 text-xs sm:text-sm">ID Number</p>
                    <p className="text-white text-sm sm:text-base">{selectedKYC.kyc_data?.id_number || 'N/A'}</p>
                  </div>
                </div>
              </div>

              {/* Documents */}
              <div className="bg-gray-800 rounded-lg p-4">
                <h4 className="text-white font-bold mb-3 text-sm sm:text-base">Submitted Documents</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4" data-testid="kyc-doc-thumbnails">
                  {['id_front', 'id_back', 'selfie_with_id', 'proof_of_address'].map((kind) => {
                    const docId = selectedKYC.kyc_documents?.[kind]
                      || selectedKYC.kyc_documents?.[kind === 'selfie_with_id' ? 'selfie' : kind];
                    if (!docId) return null;
                    return (
                      <KYCDocumentThumbnail
                        key={kind}
                        kind={kind}
                        docId={docId}
                      />
                    );
                  })}
                  {!selectedKYC.kyc_documents && (
                    <p className="text-gray-500 text-sm sm:col-span-2">No documents on file.</p>
                  )}
                </div>
              </div>

              {/* KYC Level */}
              <div>
                <label className="text-white font-semibold mb-2 block text-sm sm:text-base">KYC Level</label>
                <select
                  value={kycLevel}
                  onChange={(e) => setKycLevel(parseInt(e.target.value))}
                  className="w-full px-3 sm:px-4 py-2 bg-gray-800 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm"
                >
                  <option value={1}>Level 1 - Basic ($1,000/day limit)</option>
                  <option value={2}>Level 2 - Advanced ($10,000/day limit)</option>
                  <option value={3}>Level 3 - VIP (Unlimited)</option>
                </select>
              </div>

              {/* Feedback */}
              <div>
                <label className="text-white font-semibold mb-2 block text-sm sm:text-base">Feedback/Notes</label>
                <textarea
                  value={feedback}
                  onChange={(e) => setFeedback(e.target.value)}
                  placeholder="Optional feedback for the user..."
                  className="w-full px-3 sm:px-4 py-2 bg-gray-800 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 h-20 sm:h-24 text-sm"
                />
              </div>

              {/* Actions */}
              <div className="flex flex-col sm:flex-row items-center gap-3">
                <button
                  onClick={() => handleReview(selectedKYC.user_id, 'approved')}
                  className="w-full sm:flex-1 px-4 py-3 bg-green-600 hover:bg-green-700 rounded-lg text-white font-semibold flex items-center justify-center space-x-2 text-sm"
                >
                  <CheckCircle className="h-4 w-4 sm:h-5 sm:w-5" />
                  <span>Approve</span>
                </button>
                <button
                  onClick={() => handleReview(selectedKYC.user_id, 'rejected')}
                  className="w-full sm:flex-1 px-4 py-3 bg-red-600 hover:bg-red-700 rounded-lg text-white font-semibold flex items-center justify-center space-x-2 text-sm"
                >
                  <XCircle className="h-4 w-4 sm:h-5 sm:w-5" />
                  <span>Reject</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default KYCManagement;
